import { interpolate, InterpolationError } from '@de/utils/interpolation';
import {
  parseProductFieldMapping,
  shouldSlugifyProductField,
  type ProductFieldMapping,
  type ProductFieldParseResult,
} from './productEventMappingContract';

/**
 * Path or literal classification {@link resolveMappingValue} can resolve.
 */
export type ResolvableProductField = Extract<
  ProductFieldParseResult,
  { readonly kind: 'dataPath' } | { readonly kind: 'literal' }
>;

/**
 * Slugify a Product override the same way `createEventingPayloadsDE`
 * does: lowercase, trim, strip non-word except spaces/hyphens, collapse
 * whitespace/`_`/`-` to `-`, trim dashes.
 *
 * Payload builders still slugify `variant` / `category` / `name` at
 * emit. A second pass is idempotent; later apply work should stop
 * re-slugifying resolver output for configured keys if that becomes
 * observable.
 *
 * @param {string} text - Raw override value
 * @returns {string} Slugified value
 */
export function slugifyProductValue(text: string): string {
  return text
    .toLowerCase()
    .trim()
    .replace(/[^\w\s-]/g, '')
    .replace(/[\s_-]+/g, '-')
    .replace(/^-+|-+$/g, '');
}

/**
 * Resolve one parsed mapping against a caller-supplied root.
 *
 * `{ literal }` ignores the root. `{ dataPath }` interpolates against
 * the root (Entry `flowContext` or EL repeater `item`). Does not
 * slugify — callers apply {@link shouldSlugifyProductField} or
 * `shouldSlugifyCustomDimensionValue` for dimension values.
 *
 * @param {ResolvableProductField} parsed - Path or literal parse result
 * @param {Readonly<Record<string, unknown>> | undefined} root - Resolve root
 * @returns {string | undefined} Coerced scalar, or undefined when missing
 */
export function resolveMappingValue(
  parsed: ResolvableProductField,
  root: Readonly<Record<string, unknown>> | undefined,
): string | undefined {
  if (parsed.kind === 'literal') {
    return parsed.literal.length > 0 ? parsed.literal : undefined;
  }

  if (!root) return undefined;

  const resolved = interpolate(`{{${parsed.dataPath}}}`, root);
  if (resolved === undefined || resolved === null) return undefined;

  const coerced = coerce(resolved);
  return coerced.length > 0 ? coerced : undefined;
}

/**
 * Resolve a `tracking.productFields` map against one resolve root
 * (`flowContext` on Entry, repeater `item` on EL). Callers pass the
 * root; this function does not look up `item` or `flowContext`.
 *
 * Each entry is a legacy string path, `{ dataPath }`, or `{ literal }`
 * (literals only on `formatType` / `formatSubtype`). Missing
 * / invalid entries are omitted so the caller keeps today's defaults.
 *
 * Resolution rules:
 *   - Missing/undefined values are dropped.
 *   - Numbers and booleans are coerced via `String(value)`.
 *   - Arrays are comma-joined (matching `interpolate`); objects are
 *     dropped as authoring mistakes.
 *   - Slugify follows {@link shouldSlugifyProductField}. Never
 *     slugify `productId` / `sku` / `brand`.
 *   - Parse errors are caught per-key and warned; other entries
 *     continue resolving.
 *
 * Returns a fresh object with only the resolved entries so the caller
 * can spread the result onto the wire `product` payload.
 *
 * @param {Readonly<Record<string, ProductFieldMapping>> | undefined} productFields - Dual-shape map
 * @param {Readonly<Record<string, unknown>> | undefined} context - Resolve root
 * @returns {Record<string, string>} Sparse override map
 */
export function resolveProductFields(
  productFields: Readonly<Record<string, ProductFieldMapping>> | undefined,
  context: Readonly<Record<string, unknown>> | undefined,
): Record<string, string> {
  if (!productFields) return {};

  const out: Record<string, string> = {};

  for (const [productKey, raw] of Object.entries(productFields)) {
    const parsed = parseProductFieldMapping(productKey, raw);
    if (parsed.kind === 'empty') continue;
    if (parsed.kind === 'invalid') {
      // eslint-disable-next-line no-console
      console.warn(
        `[ConfigurableStep] tracking.productFields["${productKey}"] is invalid (${parsed.reason}); skipping.`,
      );
      continue;
    }

    let resolved: string | undefined;
    try {
      resolved = resolveMappingValue(parsed, context);
    } catch (err) {
      // Parse errors are author-time bugs — warn but don't throw so
      // the rest of the override still resolves.
      // eslint-disable-next-line no-console
      console.warn(
        `[ConfigurableStep] tracking.productFields["${productKey}"]: failed to resolve path "${
          parsed.kind === 'dataPath' ? parsed.dataPath : ''
        }" — ${err instanceof InterpolationError ? err.message : String(err)}`,
      );
      continue;
    }

    if (resolved === undefined) continue;

    const value = shouldSlugifyProductField(productKey)
      ? slugifyProductValue(resolved)
      : resolved;
    if (value.length === 0) continue;

    out[productKey] = value;
  }

  return out;
}

/**
 * Wire keys in `productFields` that did not resolve. Order follows
 * the map. Empty entries are skipped. Invalid and unresolved keys
 * are included. The resolver still warns on those separately.
 */
export function listProductFieldFallbacks(
  productFields: Readonly<Record<string, ProductFieldMapping>> | undefined,
  context: Readonly<Record<string, unknown>> | undefined,
): string[] {
  if (!productFields) return [];
  const missed: string[] = [];
  for (const [productKey, raw] of Object.entries(productFields)) {
    const parsed = parseProductFieldMapping(productKey, raw);
    if (parsed.kind === 'empty') continue;
    if (parsed.kind === 'invalid') {
      missed.push(productKey);
      continue;
    }
    let resolved: string | undefined;
    try {
      resolved = resolveMappingValue(parsed, context);
    } catch {
      missed.push(productKey);
      continue;
    }
    if (resolved === undefined) {
      missed.push(productKey);
      continue;
    }
    const value = shouldSlugifyProductField(productKey)
      ? slugifyProductValue(resolved)
      : resolved;
    if (value.length === 0) missed.push(productKey);
  }
  return missed;
}

function coerce(value: unknown): string {
  if (typeof value === 'string') return value;
  if (typeof value === 'number' || typeof value === 'boolean') {
    return String(value);
  }
  if (Array.isArray(value)) {
    // Multi-select / multi-formKey selections come through as arrays.
    // Comma-join matches `interpolate()`'s mixed-template coercion
    // (null/undefined → ''; does not filter empties — callers rarely
    // emit sparse selection arrays).
    return value
      .map((v) =>
        v === null || v === undefined
          ? ''
          : typeof v === 'object'
            ? ''
            : String(v),
      )
      .filter(Boolean)
      .join(',');
  }
  // Objects on a leaf path are almost always an authoring mistake —
  // collapse to empty so the payload builder's slugify fallback takes
  // over instead of emitting `[object Object]`.
  return '';
}
